/**
 * LocalStorage utilities for managing recent entity searches
 */

import { normalizeEntityType } from './normalizeEntityType';
import type { EntityResult, RecentSearchItem, SearchContext } from './types';
import { isEntityType } from './utils';

const MAX_RECENT_ITEMS = 10;

/**
 * Get the localStorage key for recent searches
 */
export function getStorageKey(orgId: number, context: SearchContext): string {
  return `ahoy:search:recents:${orgId}:${context}`;
}

/**
 * Validate that a parsed item matches the expected shape
 */
function isValidRecentSearchItem(item: unknown): item is RecentSearchItem {
  if (!item || typeof item !== 'object') {
    return false;
  }

  const obj = item as Record<string, unknown>;

  // Validate selected_at timestamp
  if (typeof obj.selected_at !== 'number') {
    return false;
  }

  // Validate entity object
  if (!obj.entity || typeof obj.entity !== 'object') {
    return false;
  }

  const entity = obj.entity as Record<string, unknown>;

  // Validate required base fields
  // Accept both string (legacy) and number (current) for backward compatibility
  if (typeof entity.id !== 'string' && typeof entity.id !== 'number') {
    return false;
  }
  if (typeof entity.name !== 'string') {
    return false;
  }
  if (typeof entity.type !== 'string') {
    return false;
  }

  // Normalize legacy types before validation
  const normalizedType = normalizeEntityType(entity.type);
  // Reject items with unknown types (normalizeEntityType warns and returns original)
  if (!isEntityType(normalizedType)) {
    return false;
  }
  // Update entity object with canonical type
  entity.type = normalizedType;

  return true;
}

/**
 * Retrieve recent searches from localStorage
 * Returns empty array if data is corrupted or missing
 */
export function getRecentSearches(
  orgId: number,
  context: SearchContext
): EntityResult[] {
  try {
    const key = getStorageKey(orgId, context);
    const stored = localStorage.getItem(key);

    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored);

    // Validate it's an array
    if (!Array.isArray(parsed)) {
      return [];
    }

    // Filter to only valid items and extract entities
    const validItems = parsed.filter(isValidRecentSearchItem);

    // Sort by selected_at descending (most recent first)
    validItems.sort((a, b) => b.selected_at - a.selected_at);

    // Return just the entities, limited to max items
    return validItems.slice(0, MAX_RECENT_ITEMS).map(item => item.entity);
  } catch {
    // Handle JSON parse errors or other localStorage issues
    return [];
  }
}

/**
 * Add an entity to recent searches
 * Deduplicates by type+id and maintains max item limit
 */
export function addRecentSearch(
  orgId: number,
  context: SearchContext,
  entity: EntityResult
): void {
  try {
    const key = getStorageKey(orgId, context);
    const stored = localStorage.getItem(key);

    let items: RecentSearchItem[] = [];

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          items = parsed.filter(isValidRecentSearchItem);
        }
      } catch {
        // Start fresh if stored data is corrupted
        items = [];
      }
    }

    // Remove existing entry for this entity (deduplicate by entity_id)
    // entity_id is the stable identifier across all entity types
    // Fallback to type+id comparison for legacy stored items without entity_id
    items = items.filter(item => {
      // Both have entity_id: use it (primary path)
      if (item.entity.entity_id && entity.entity_id) {
        return item.entity.entity_id !== entity.entity_id;
      }
      // Either lacks entity_id: fall back to legacy type+id comparison
      return !(
        item.entity.type === entity.type &&
        String(item.entity.id) === String(entity.id)
      );
    });

    // Add new item at the beginning
    const newItem: RecentSearchItem = {
      entity,
      selected_at: Date.now(),
    };
    items.unshift(newItem);

    // Limit to max items
    items = items.slice(0, MAX_RECENT_ITEMS);

    localStorage.setItem(key, JSON.stringify(items));
  } catch {
    // Silently fail on localStorage quota errors or other issues
    // This is non-critical functionality
  }
}

/**
 * Clear all recent searches for an org+context
 */
export function clearRecentSearches(
  orgId: number,
  context: SearchContext
): void {
  try {
    const key = getStorageKey(orgId, context);
    localStorage.removeItem(key);
  } catch {
    // Silently fail on localStorage issues
  }
}
